import { Dispatch, SetStateAction, useRef, useState } from 'react';
import { motion, TargetAndTransition } from 'framer-motion';
import AnimatedEagleEye from './AnimatedEagleEye';
import { LinkConfig } from '../Header';
import {
  NavigationMenuContent,
  NavigationMenuLink,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '../ui/navigation-menu';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/hooks/use-Theme';
import Circuit from './Circuit';
import { Menu } from 'lucide-react';

type Props = {
  expanded: boolean;
  linkList: LinkConfig[];
  setExpanded: Dispatch<SetStateAction<boolean>>;
};

export default function AnimatedNavbar({ expanded, linkList, setExpanded }: Props) {
  const [eyeRdy, setEyeRdy] = useState(false);
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkTheme = theme === 'dark';
  const eagleEyeRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<HTMLButtonElement[]>([]);
  const pathAnimationDelay = 1.15;
  const pathAnimationDuration = 0.25;
  const btnAnimationDuration = 0.25;
  const screenSize = window.innerWidth < 768 ? 'small' : window.innerWidth < 1024 ? 'medium' : 'large';
  const lineWidth = 2;
  const lightThemeColor = '#3367A0';
  const darkThemeColor = '#B9C0C2';
  const pathColor = isDarkTheme ? darkThemeColor : lightThemeColor;

  const renderEagleAnimation = () => {
    const initial: TargetAndTransition = {
      scale: 1,
      top: '50%',
      left: '50%',
      position: 'absolute',
      transform: 'translate(-50%, -50%)',
      width: screenSize === 'small' ? '12svw' : '7svw',
      height: screenSize === 'small' ? '12svw' : '7svw',

      margin: '0 2rem 0 1rem',
    };

    const fixed: TargetAndTransition = {
      top: '-50%',
      left: '0',
      transform: 'translate(0%, 0%)',

      width: '1svh',
      height: '1svh',
      marginLeft: '1rem',
      position: 'relative',
      margin: '0 2rem 0 1rem',
    };

    return (
      <motion.div
        className={cn('z-10 flex rounded-full ', expanded ? '' : 'hidden md:block')}
        style={{
          cursor: expanded ? '' : 'pointer',
        }}
        onClick={() => !expanded && navigate('/')}
        initial={expanded ? initial : fixed}
        transition={{ duration: 0 }}
        animate={expanded ? initial : fixed}>
        <AnimatedEagleEye ref={eagleEyeRef} setEyeRdy={setEyeRdy} expanded={expanded} />
      </motion.div>
    );
  };

  const buttonList = linkList.map((link, idx) => {
    const highlightStyleIfActive = location.pathname === link.path ? 'bg-secondary/70 ' : '';
    const isDisabled = link.disabled || false;

    return (
      <motion.button
        key={link.label}
        onClick={() => {
          if (!isDisabled) {
            navigate(link.path);
            setExpanded(false);
          }
        }}
        ref={(el) => {
          if (el) buttonRefs.current[idx] = el;
        }}
        initial={expanded ? { ...link.position(screenSize), position: 'absolute', opacity: 0 } : false}
        // styles if the the navbar is not expanded
        className={cn(
          !expanded && navigationMenuTriggerStyle(),
          !expanded ? highlightStyleIfActive : '',
          !expanded && 'w-full min-w-fit font-medium tracking-wider cursor-pointer',
          !expanded && isDisabled ? 'opacity-50 cursor-not-allowed hover:bg-transparent' : '',
          'rounded  z-[110]',
          expanded && ' w-fit'
        )}
        animate={
          expanded
            ? { ...link.position(screenSize), opacity: 1, color: 'rgb(192,192,192)' }
            : {
                position: 'relative',
                top: 'none',
                left: 'none',
              }
        }
        transition={{
          duration: expanded ? btnAnimationDuration : 0.15,
          delay: expanded ? pathAnimationDelay + pathAnimationDuration + btnAnimationDuration : 0,
        }}>
        <NavigationMenuLink
          key={link.label}
          className={cn(
            expanded && 'bg-transparent rounded ',
            expanded && ' inline-flex py-2 px-4 text-center md:text-xl border-2 rounded-lg border-secondaryColor/50',
            expanded &&
              'dark:hover:text-white hover:text-black dark:bg-gray-900 bg-[#B9C0C2] text-secondary-foreground/80'
          )}>
          {link.label}
        </NavigationMenuLink>
        {/* glowing background */}
        <motion.div
          className="absolute px-6 py-3 w-full h-full rounded-lg  z-[-1]"
          initial={{
            boxShadow: '3px 5px 7px -3px  rgba(184, 226, 254, 0)',
            top: '0%',
            left: '0%',
            position: 'absolute',
          }}
          transition={{
            duration: expanded ? btnAnimationDuration + 1 : 0,
            delay: expanded ? pathAnimationDelay + pathAnimationDuration + btnAnimationDuration + 0.25 : 0,
          }}
          animate={
            expanded
              ? isDarkTheme
                ? { boxShadow: '3px 5px 12px -1px rgba(184, 226, 254, 0.2)' }
                : { boxShadow: '3px 5px 12px -1px rgb(136 207 254)' }
              : { opacity: 0 }
          }
        />
      </motion.button>
    );
  });

  const desktopLayout = () => {
    return <div className="hidden md:flex gap-4 z-[100]">{buttonList}</div>;
  };

  const mobileLayout = () => (
    <>
      <div id="navigation-items" className="md:hidden items-center gap-2">
        <NavigationMenuTrigger className="[&>svg]:hidden p-1 ml-2">
          <div>
            <Menu size={'1.5rem'} className="opacity-75" />
          </div>
        </NavigationMenuTrigger>
        <NavigationMenuContent className="ml-auto flex flex-col justify-center items-center !w-[99svw] !max-w-full gap-2 p-2">
          {buttonList}

          {/* Home Button */}
          <NavigationMenuLink
            onClick={() => navigate('/')}
            className={cn(
              navigationMenuTriggerStyle(),
              'w-full min-w-fit font-medium tracking-wider cursor-pointer mt-8'
            )}>
            Home
          </NavigationMenuLink>
        </NavigationMenuContent>
      </div>
    </>
  );

  return (
    <>
      <div
        className="relative h-full w-full overflow-hidden flex items-end justify-evenly gap-4"
        style={{
          opacity: eyeRdy ? 1 : 0,
        }}>
        {/* Eagle Head with eye */}
        {renderEagleAnimation()}

        {/* Buttons */}
        {!expanded ? mobileLayout() : null}
        {!expanded ? desktopLayout() : null}
        {expanded ? buttonList : null}

        {/* Draw circuit from the corner */}
      </div>
      {expanded && eyeRdy ? (
        <>
          <div className={'absolute top-0 left-0 w-[100svw] h-[100svh] pointer-events-none z-[1] border-2'}>
            <div
              className={cn(
                screenSize === 'small'
                  ? 'absolute top-[-10%] left-[-10%] w-[120svw] h-[120svh] pointer-events-none z-[2] '
                  : 'absolute top-0 left-0 w-full h-full pointer-events-none z-[2] '
              )}>
              <Circuit
                strokeWidth={lineWidth}
                delay={pathAnimationDelay + pathAnimationDuration}
                color={pathColor}
                duration={pathAnimationDuration}
              />
            </div>
            <div
              className={cn(
                screenSize === 'small'
                  ? 'absolute top-[-10%] left-[-10%] w-[120svw] h-[120svh] pointer-events-none z-[2] '
                  : 'absolute top-0 left-0 w-full h-full pointer-events-none z-[2] '
              )}>
              <Circuit strokeWidth={lineWidth} color={pathColor} styleInactive={true} />
            </div>
          </div>
          <div
            className={cn(
              screenSize === 'small'
                ? 'absolute top-[-30%] left-[-23%] w-[75svw] h-[75svh] pointer-events-none z-[1] '
                : 'absolute top-[-15%] left-[-20%] w-[45svw] h-[45svh] pointer-events-none z-[1] '
            )}
            style={{ transform: 'rotate(135deg)' }}>
            <Circuit strokeWidth={lineWidth} color={pathColor} styleInactive={true} />
          </div>
        </>
      ) : null}
    </>
  );
}
